/** @format */

/* eslint @typescript-eslint/prefer-nullish-coalescing: "warn" -- Legacy code: do not use as an example. */
import isPropValid from '@emotion/is-prop-valid';
import { Heading } from '@mpb-com/feather/components/atoms/heading';
import { HeadingSize } from '@mpb-com/feather/components/atoms/heading/types';
import {
    Skeleton,
    SkeletonVariant,
} from '@mpb-com/feather/components/atoms/skeleton';
import { Thumbnail } from '@mpb-com/feather/components/atoms/thumbnail';
import { ThumbnailSkeleton } from '@mpb-com/feather/components/atoms/thumbnail/skeleton';
import { useTranslation } from 'next-i18next';
import React, {
    ForwardRefRenderFunction,
    Fragment,
    MouseEvent,
    Ref,
    forwardRef,
    useRef,
} from 'react';
import styled from 'styled-components';

import { CloudflareImageSize } from '@toucan/client/global/cloudflare';
import { fallbackImagePath } from '@toucan/client/global/product';

import { CardBackground } from '@toucan/client/shared/components/card-background';
import { Link } from '@toucan/client/shared/components/link';
import {
    ElementBreakpointsQuery,
    useElementBreakpoints,
} from '@toucan/client/shared/hooks/use-element-breakpoints';

import { CloudflareImage } from '@toucan/client/blocks/components/cloudflare-image';
import { TruncateText } from '@toucan/client/blocks/components/truncate-text';

import { FavouriteModelButton } from '../favourite-model-button';
import { getFormattedPrice } from './helpers';
import { styles } from './model-card.styles';
import { ModelCardProps, modelCardBreakpoints } from './types';

const StyledModelCard = styled('div').withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})`
    ${styles}
`;

const ModelCardWithRef: ForwardRefRenderFunction<
    HTMLDivElement,
    ModelCardProps
> = (
    {
        modelListing,
        onCreateAlert,
        'data-testid': dataTestId = 'model-card',
        ...otherProps
    }: ModelCardProps,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- Legacy code: do not use as an example.
    ref: Ref<HTMLDivElement>
) => {
    const modelCardRef = useRef<HTMLDivElement | null>(null);

    const { t } = useTranslation();

    const { id, name, description, image, quantity } = modelListing || {};

    const isLoading = Boolean(!modelListing);

    const isOutOfStock = quantity === 0;

    const modelCardBreakpointsState = useElementBreakpoints({
        ref: modelCardRef,
        breakpoints: modelCardBreakpoints,
        query: ElementBreakpointsQuery.WIDTH,
    });

    const handleCreateAlert = (event: MouseEvent<HTMLElement>) => {
        event.preventDefault();
        event.stopPropagation();

        if (!modelListing) return;

        onCreateAlert?.(modelListing);
    };

    const renderThumbnail = () => {
        const aspectRatio = modelCardBreakpointsState.small ? '4/3' : undefined;

        return (
            <Thumbnail
                data-testid="model-card__thumbnail"
                className="thumbnail"
                height="20rem"
                aspectRatio={aspectRatio}
            >
                {isLoading || !image ? (
                    <ThumbnailSkeleton
                        data-testid="model-card__thumbnail-skeleton"
                        className="thumbnail skeleton"
                    />
                ) : (
                    <CloudflareImage
                        src={image}
                        fallbackSrc={fallbackImagePath}
                        alt={name ?? ''}
                        imageSize={CloudflareImageSize.THUMBNAIL}
                    />
                )}
            </Thumbnail>
        );
    };

    const renderTitle = () => {
        if (isLoading) {
            return (
                <Skeleton
                    variant={SkeletonVariant.TEXT}
                    lineCount={1}
                    className="title skeleton"
                    data-testid="model-card__title-skeleton"
                />
            );
        }

        return (
            <Heading
                size={HeadingSize.H4}
                className="title"
                data-testid="model-card__title"
            >
                {name}
            </Heading>
        );
    };

    const renderDescription = () => (
        <TruncateText
            className="description"
            data-testid="model-card__description"
            lines={7}
            isLoading={isLoading}
            hideExpander
        >
            {description}
        </TruncateText>
    );

    const renderPrice = () => {
        if (isLoading) {
            return (
                <Skeleton
                    variant={SkeletonVariant.TEXT}
                    lineCount={1}
                    className="price skeleton"
                    data-testid="model-card__price-skeleton"
                />
            );
        }

        const outOfStockMessage = (
            <Fragment>
                <span>
                    {t('Out of stock.', { context: 'ModelCard' })}&nbsp;
                </span>
                <Link
                    onClick={handleCreateAlert}
                    data-testid="model-card__create-alert-button"
                >
                    {t('Create alert')}
                </Link>
            </Fragment>
        );

        return (
            <div className="price" data-testid="model-card__price">
                {isOutOfStock
                    ? outOfStockMessage
                    : getFormattedPrice({
                          modelListing,
                          t,
                      })}
            </div>
        );
    };

    const renderButton = () => {
        return (
            <FavouriteModelButton
                isLoading={isLoading}
                modelId={id}
                className="button"
            />
        );
    };

    return (
        <StyledModelCard
            modelCardBreakpointsState={modelCardBreakpointsState}
            data-testid={dataTestId}
            isOutOfStock={isOutOfStock}
            {...otherProps}
        >
            <CardBackground className="expanding-card-background">
                <div
                    className="content-container"
                    data-testid="model-card__content-container"
                    ref={modelCardRef}
                >
                    {renderThumbnail()}
                    {renderTitle()}
                    {renderDescription()}
                    {renderPrice()}
                    {renderButton()}
                </div>
            </CardBackground>
        </StyledModelCard>
    );
};

export const ModelCard = forwardRef(ModelCardWithRef);
